import { Card, CardBody, Chip } from "@heroui/react";
import { useTranslation } from 'react-i18next';
import { ReadOnlyRating } from '../ReadOnlyRating';
import { PlaceFollowButton } from '~/components/molecules/PlaceFollowButton';
import { ROUTES } from '~/constants/routes.contants';
import { PlaceResponseDto } from "@bitecritic-api/src/places/dto/place.response.dto";
import { CountryFlag } from '~/components/atoms/CountryFlag';
import { titleAndIdToSlug } from "~/utils/slug-utils";
import { BaseLink } from "~/components/atoms/Link";
import { FaMapPin } from "react-icons/fa";

interface PlaceCardSmallProps {
  place: PlaceResponseDto
  className?: string;
  showFollowButton?: boolean;
  showRating?: boolean;
}

export const PlaceCardSmall = ({ place, className, showFollowButton = true, showRating = true }: PlaceCardSmallProps) => {
  const { t } = useTranslation();
  const maxWidth = showRating ? 'calc(100% - 20px)' : 'calc(100%)';

  return (
    <Card
      className={`w-full mb-4 hover:shadow-lg transition-shadow group cursor-pointer bg-secondary-50 border border-secondary-200 overflow-hidden ${className || ''}`}
      autoFocus={false}
    >
      <CardBody className="p-4">
        <div className="flex gap-4 flex-wrap sm:flex-nowrap">
          {/* Main Content */}
          <BaseLink
            href={`${ROUTES.PLACES}/${titleAndIdToSlug(place.name, place.id)}`} className="flex-1 min-w-0">
            <div className={`flex justify-between items-start gap-2 ${maxWidth}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <FaMapPin className="w-4 h-4 text-secondary-600" />
                  <h3 className="font-semibold text-base text-foreground group-hover:text-warning-600 transition-colors truncate text-wrap">
                    {place.name}
                  </h3>
                  {place.country && (
                    <CountryFlag
                      countryCode={place.country.code}
                      countryName={place.country.name}
                      size="sm"
                    />
                  )}
                  {typeof place.isOpenNow === 'boolean' && (
                    <Chip className={`text-sm ${place.isOpenNow ? 'bg-success-50 text-success-600' : 'bg-danger-50 text-danger-600'}`}>
                      {place.isOpenNow ? t('common.open') : t('common.closed')}
                    </Chip>
                  )}
                </div>
                <p className="text-sm text-secondary-800 break-words line-clamp-2">{place.address}</p>
              </div>
            </div>
          </BaseLink>

          {showFollowButton && showRating && (
            <div className="flex flex-col items-end justify-start min-w-[80px] shrink-0">
              {showRating && place.reviewOverallRating > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-500">({place.totalReviews})</span>
                  <ReadOnlyRating rating={place.reviewOverallRating} size="sm" />
                </div>
              )}
              {showFollowButton && place.totalReviews > 0 && (
                <>
                  <div className="flex mt-2">
                    <PlaceFollowButton placeId={place.id} size="sm" />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};